import { action } from '@ember/object';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import dateUtils from 'donor-elf-web/lib/date-utils';
import type { GoalDataTotalAndExpecting } from 'donor-elf-web/models/goal';
import GoalModel from 'donor-elf-web/models/goal';
import FormatAmountService from 'donor-elf-web/services/format-amount-service';
import FundsService from 'donor-elf-web/services/funds-service';
import Settings from 'donor-elf-web/services/settings';
import Highcharts from 'highcharts';
import moment from 'moment';

interface ComponentArgs {
  goal: GoalModel;
}

export default class TotalAndExpectingRaisedByMonthChart extends Component<ComponentArgs> {
  @service declare formatAmountService: FormatAmountService;
  @service declare fundsService: FundsService;
  @service declare settings: Settings;

  @action
  chartDivDidInsert(el: any) {
    next(() => {
      Highcharts.chart(el, this.chartSettings);
    });
  }

  get selectedFund() {
    return this.fundsService.selectedFund;
  }

  get showBalance() {
    return !this.settings.orgSettings!.incomeOnly;
  }

  get goalData(): GoalDataTotalAndExpecting {
    return this.args.goal.goalData;
  }

  get runningGivenByMonth() {
    return this.goalData.running_given_by_month;
  }

  get goalAmount() {
    return this.args.goal.goalAmount;
  }

  get goalDate() {
    return dateUtils.deserializeDateOnly(this.goalData.goal_date);
  }

  get series() {
    let { runningGivenByMonth, goalAmount, goalDate } = this;
    let firstMonth = dateUtils.deserializeDateOnly(
      runningGivenByMonth[0].month,
    );
    let lastGivenMonth = dateUtils.deserializeDateOnly(
      runningGivenByMonth[runningGivenByMonth.length - 1].month,
    );
    let series = [
      {
        type: 'area',
        color: '#0b68a1',
        name: 'Given',
        marker: {
          enabled: false,
        },
        data: runningGivenByMonth.map((item) => {
          let date = dateUtils.deserializeDateOnly(item.month);
          return [Number(date), item.total_given];
        }),
      },
      {
        type: 'line',
        color: '#f0ad4e',
        name: 'Goal',
        lineWidth: 5,
        marker: {
          enabled: false,
        },
        data: [
          [Number(firstMonth), goalAmount],
          [Number(goalDate), goalAmount],
        ],
      },
    ];
    if (goalDate > new Date()) {
      series.unshift({
        type: 'area',
        color: '#d0eafb', //'#e6e6e6',
        lineWidth: 2,
        name: 'Given & Expecting',
        marker: {
          enabled: false,
        },
        data: [
          [Number(lastGivenMonth), this.goalData.given_amount],
          [
            Number(goalDate),
            this.goalData.given_amount + this.goalData.expected_amount,
          ],
        ],
      });
    }
    return series;
  }

  get chartSettings() {
    let self = this;
    return {
      chart: {
        height: 225,
        // margin: [2, 0, 2, 0],
        borderWidth: 0,
      },
      title: null,
      credits: {
        enabled: false,
      },
      xAxis: {
        type: 'datetime',
      },
      yAxis: {
        min: 0,
        maxPadding: 0.0,
        endOnTick: false,
        title: {
          text: null,
        },
      },
      legend: {
        enabled: false,
      },
      tooltip: {
        formatter: function () {
          let date = moment(this.x);
          return `${date.format('MMMM YYYY')}<br/>${this.series.name}: <b>${self.formatAmountService.format(this.y)}</b>`;
        },
      },
      plotOptions: {},
      series: this.series,
    };
  }
}
